import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { Button } from 'components/base';
import useModalContext from 'components/context/modal';

import usersApi from '../../api/users';
import useUserContext from '../../context/user';

// react-image-crop (component + its CSS) is only needed once the user picks a
// file, so keep it out of the /user page bundle.
const AvatarCropModal = dynamic(() => import('./avatarCropModal'), { ssr: false });

// Mirror of the backend limits (photolane-backend docs/avatars.md): allowed
// source content types and the 10 MB presigned-POST cap.
const AVATAR_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const AVATAR_MAX_BYTES = 10 * 1024 * 1024;

// Avatar processing is asynchronous — after confirm we re-fetch the user until
// avatar_url flips to the new variant (typically a few seconds).
const POLL_INTERVAL = 2000;
const POLL_MAX_ATTEMPTS = 15;

// Polling and the post-removal fetch run while the user may save other fields on the page,
// so only the avatar fields are taken from their responses.
const avatarFields = (user) => ({
  avatar_url: user.avatar_url,
  avatar_url_2x: user.avatar_url_2x,
});

/**
 * Avatar upload with a square crop step, and removal, for the /user pages of
 * both apps. Shows the cropped image optimistically (`previewUrl`) while the
 * backend renders the served variants.
 *
 * Also refreshes the user from GET users/me once on mount. The parabo-web
 * Personal Info page relies on that refresh for every user field it shows
 * (e.g. `credit_balance` in the delete account panel).
 *
 * The caller renders `<input {...inputProps} />` and calls `openPicker()`.
 */
const useAvatarUpload = (accessToken) => {
  const modalContext = useModalContext();
  const userContext = useUserContext();

  const fileInputRef = useRef(null);
  const pollTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const localPreviewRef = useRef(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // The upload chain deliberately keeps running after unmount so a confirmed
  // avatar is not lost — only polling, new timeouts and the preview blob URL
  // are cut off here. The ref is re-armed in the setup phase so StrictMode's
  // dev-only effect replay (setup → cleanup → setup) doesn't leave it false.
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      clearTimeout(pollTimerRef.current);
      if (localPreviewRef.current) {
        URL.revokeObjectURL(localPreviewRef.current);
      }
    };
  }, []);

  // The user store may be restored from the session cache without a
  // getDetails() call, so avatar_url can be stale (e.g. changed on another
  // device). Refresh once on mount.
  useEffect(() => {
    if (!accessToken) {
      return;
    }

    (async () => {
      const { data } = await usersApi(accessToken).getDetails();
      if (data) {
        userContext.setData(data);
      }
    })();
  }, []);

  const replaceLocalPreview = (url) => {
    if (localPreviewRef.current) {
      URL.revokeObjectURL(localPreviewRef.current);
    }
    localPreviewRef.current = url;
    setLocalPreview(url);
  };

  const pollForNewAvatar = (previousUrl, attempt = 0) => {
    if (!isMountedRef.current) {
      return;
    }

    if (attempt >= POLL_MAX_ATTEMPTS) {
      // Processing did not finish in time (or the upload was discarded as
      // undecodable) — drop the optimistic preview and fall back to reality.
      replaceLocalPreview(null);
      setIsSaving(false);
      return;
    }

    pollTimerRef.current = setTimeout(async () => {
      const { data } = await usersApi(accessToken).getDetails();

      if (!isMountedRef.current) {
        return;
      }

      if (data && data.avatar_url && data.avatar_url !== previousUrl) {
        userContext.mergeData(avatarFields(data));
        replaceLocalPreview(null);
        setIsSaving(false);
      } else {
        pollForNewAvatar(previousUrl, attempt + 1);
      }
    }, POLL_INTERVAL);
  };

  const handleUploadFailed = () => {
    replaceLocalPreview(null);
    setIsSaving(false);
    modalContext.toast('We could not upload your photo. Please try again.', { type: 'warning' });
  };

  const uploadCroppedAvatar = async (blob) => {
    setIsSaving(true);
    replaceLocalPreview(URL.createObjectURL(blob));

    const api = usersApi(accessToken);

    const { data: target, error: targetError } = await api.getAvatarUploadUrl(blob.type);
    if (targetError) {
      handleUploadFailed();
      return;
    }

    const { error: uploadError } = await api.uploadAvatarToS3(target, blob);
    if (uploadError) {
      handleUploadFailed();
      return;
    }

    const { data: user, error: confirmError } = await api.confirmAvatar(target.key);
    if (confirmError) {
      handleUploadFailed();
      return;
    }

    // The confirm response still carries the previous avatar_url.
    pollForNewAvatar(user.avatar_url);
  };

  const openCropModal = (src) => {
    const submitRef = { current: null };

    modalContext.show(
      <AvatarCropModal src={src} submitRef={submitRef} onCropped={uploadCroppedAvatar} />,
      {
        title: 'Crop Your Photo',
        close: () => URL.revokeObjectURL(src),
        footer: ({ close }) => (
          <>
            <Button type="outline" onClick={close}>
              Cancel
            </Button>
            <Button type="primary" onClick={() => submitRef.current?.(close)}>
              Save
            </Button>
          </>
        ),
      },
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (!file) {
      return;
    }

    if (!AVATAR_CONTENT_TYPES.includes(file.type)) {
      modalContext.toast('Please choose a JPEG, PNG or WebP image.', { type: 'warning' });
      return;
    }

    if (file.size > AVATAR_MAX_BYTES) {
      modalContext.toast('The photo is too large. The maximum size is 10 MB.', { type: 'warning' });
      return;
    }

    openCropModal(URL.createObjectURL(file));
  };

  const remove = () => {
    modalContext.confirm('Do you really want to remove your profile photo?', {
      title: 'Remove Photo',
      confirmLabel: 'Remove',
      type: 'danger',
      onConfirm: async () => {
        const { error } = await usersApi(accessToken).deleteAvatar();

        if (error) {
          modalContext.toast('We could not remove your photo. Please try again.', {
            type: 'warning',
          });
          return;
        }

        clearTimeout(pollTimerRef.current);
        replaceLocalPreview(null);
        setIsSaving(false);

        const { data } = await usersApi(accessToken).getDetails();
        if (data) {
          userContext.mergeData(avatarFields(data));
        }
      },
    });
  };

  return {
    inputProps: {
      ref: fileInputRef,
      type: 'file',
      accept: AVATAR_CONTENT_TYPES.join(','),
      onChange: handleFileChange,
      style: { display: 'none' },
    },
    openPicker: () => fileInputRef.current?.click(),
    remove,
    isSaving,
    previewUrl: localPreview,
  };
};

export default useAvatarUpload;
